import { pendingComponentLabel } from '@repo/app-constants';
import { parseDate } from '@repo/utils';
import { format } from 'date-fns';
import { useRef, type ReactNode } from 'react';
import { Icon } from '../../Icon';
import { Popover } from '../../Popover/Popover';
import { Typography } from '../../Typography';
import styles from './PendingResultsInfo.module.css';

/**
 * Joins test names into the popover sentence's subject, e.g. "A, B and C".
 * Uses the shared component label map so every portal names a test the same
 * way.
 */
export const formatComponentNames = (components: string[]): string => {
  const labels = components.map(pendingComponentLabel);
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

interface ResultsInfoRowProps {
  /** Row text. A `date` is formatted and takes precedence over this. */
  label?: string;
  /** Date shown as the row's text, formatted as the other Details rows are. */
  date?: string | null;
  /** Popover body shown when the row or its icon is clicked. */
  content: ReactNode;
  /** Screen-reader name for the trigger. */
  ariaLabel: string;
}

/**
 * A clickable "<text> (i)" row for a kit's Details cell. Mirrors
 * {@link ShippingDetails}' trigger exactly: purple text, trailing info icon,
 * click opens a popover whose arrow points at that icon.
 */
export const ResultsInfoRow = ({
  label,
  date,
  content,
  ariaLabel,
}: ResultsInfoRowProps) => {
  const iconRef = useRef<HTMLSpanElement>(null);

  const rowText = date ? format(parseDate(date), 'MMM d, yyyy') : (label ?? '');

  return (
    <Popover
      content={<div className={styles.popoverContent}>{content}</div>}
      className={styles.popoverTrigger}
      showArrow
      arrowTargetRef={iconRef}
    >
      <div className={styles.infoRow} aria-label={ariaLabel}>
        <Typography
          variant='label'
          color='purple'
          className={styles.infoLabel}
          title={rowText}
        >
          {rowText}
        </Typography>
        <span ref={iconRef} className={styles.detailsIcon}>
          <Icon name='infoCircle' size='s' />
        </span>
      </div>
    </Popover>
  );
};

interface PendingResultsInfoProps {
  /**
   * `sampling_type` of every component still lacking results, as the backend's
   * `pending_components` virtual reports it. `undefined` when the kit was
   * fetched without its variants eager-loaded.
   */
  pendingComponents?: string[];
  /** Row text. A `date` is formatted and takes precedence over this. */
  label?: string;
  /** Date shown as the row's text, for cells that lead with one. */
  date?: string | null;
}

/**
 * The message naming a kit's outstanding tests.
 *
 * Shared so the Details-column row and the Status pill's hover say exactly the
 * same thing — they are the same information in two places.
 */
export const PendingResultsMessage = ({
  pendingComponents,
}: {
  pendingComponents: string[];
}) => (
  <Typography variant='body-xs'>
    {`${formatComponentNames(pendingComponents)} results aren't ` +
      `available yet. We'll notify you when they are added to the report.`}
  </Typography>
);

/**
 * The Details-column row naming a kit's still-outstanding tests.
 *
 * Renders nothing when there is nothing outstanding — including the
 * not-eager-loaded case, where the backend reports `undefined` rather than
 * risking a wrong answer, so an unknown state makes no claim either way.
 */
export const PendingResultsInfo = ({
  pendingComponents,
  label,
  date,
}: PendingResultsInfoProps) => {
  if (!pendingComponents?.length) return null;

  return (
    <ResultsInfoRow
      label={label}
      date={date}
      ariaLabel='View pending results'
      content={<PendingResultsMessage pendingComponents={pendingComponents} />}
    />
  );
};

interface UpdatedResultsInfoProps {
  /** When the kit's last outstanding component landed. */
  date: string;
}

/**
 * The Details-column row for a kit that was released partial and has since
 * been completed. Same trigger as {@link PendingResultsInfo}, but its message
 * is in the past tense — nothing is outstanding any more.
 */
export const UpdatedResultsInfo = ({ date }: UpdatedResultsInfoProps) => (
  <ResultsInfoRow
    date={date}
    ariaLabel='View results update'
    content={
      <Typography variant='body-xs'>
        The remaining results have been added to the report.
      </Typography>
    }
  />
);
